import React, { useMemo, useState } from "react";
import './style.scss';
import { GorupbarOrderItemDraft } from "../GroupbarOrderItem";
import { useTranslation } from "react-i18next";
import { ACCOUNT_ORDER_STATUS, ORDER_TYPE, PICKUP_STATE, TYPE_MARKER } from "../../../utils/enums";
import uuid from "draft-js/lib/uuid.js";
import { closestCorners, DndContext } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Modal } from "antd";
import { Avatar } from "../Avatar/Avatar";
import { getFullName, getUserById } from "../../../services/userModel";
import VehicleIcon from "../VehicleIcon";
import IconSwitchControl from "../IconSwitchControl";
import Markers from "../../../services/markers";
import { getDepotByIdMap } from "../../../services/restaurant";
import Map from "../Map";

const AssociatePopupDraft = ({ orders, estimatedPriceInfo, onDisband, setOrders, onRemoveOrder, courierId, selectedOrderIds, setModified, estimation, setEstimation, showEditOrder, showAssociate, onAssociate, showExtraButton, onChangeState }) => {
    const [modal, contextHolder] = Modal.useModal();
    const { t } = useTranslation();

    const [isMapMode, setIsMapMode] = useState(false);

    const courier = useMemo(() => {
        return getUserById(courierId);
    }, [courierId]);


    const onAddPickup = (order, index) => {
        if (index === undefined) return;
        if (order.status !== undefined && order.status > ACCOUNT_ORDER_STATUS.ready) {
            modal.error({
                title: t('draft.createPickupError'),
            });
            return;
        }
        handleSetModified();
        const newOrder = {
            id: uuid(),
            depotId: order.depot_id || '',
            targetTime: '',
            items: [order.id],
            type: ORDER_TYPE.pickUp,
            state: PICKUP_STATE.pending,
        };
        setOrders([...orders.slice(0, index), newOrder, ...orders.slice(index)]);
    }

    const deleteDeliveryOrder = (order, index) => {
        if(onDisband) return onDisband(order.id);

        if (index === undefined) return;

        const deleteHandle = () => {
            handleSetModified();

            const updatedOrders = [...orders];
            updatedOrders.splice(index, 1);
            setOrders(updatedOrders);
            onRemoveOrder(order.id);
        }

        if (order.status === ACCOUNT_ORDER_STATUS.delivering) {
            modal.confirm({
                title: t('draft.confirmDeleteDeliveringOrderModalTitle'),
                onOk: () => deleteHandle(),
                type: 'error',
            });
            return;
        }

        deleteHandle();
    };

    const deletePickupOrder = (item, index) => {
        if (index === undefined) return;

        const pickupOrdersBeforeCurrentPickup = orders
            .slice(0, index)
            .filter((order) => 'type' in order && order.depotId === item.depotId);

        const hasPendingPickup = !!pickupOrdersBeforeCurrentPickup.find(
            order => order.state === PICKUP_STATE.pending
        );

        if (hasPendingPickup) {
            handleSetModified();

            const updatedOrders = [...orders];
            updatedOrders.splice(index, 1);
            setOrders(updatedOrders);

            return;
        }

        const ordersAfterCurrentPickup = orders.slice(index + 1);

        const indexOfNextSimilarPickup = ordersAfterCurrentPickup.findIndex(
            order => 'type' in order && order.depotId === item.depotId && order.state === PICKUP_STATE.pending
        );

        const ordersBetweenPickups =
            indexOfNextSimilarPickup === -1
                ? ordersAfterCurrentPickup
                : ordersAfterCurrentPickup.slice(0, indexOfNextSimilarPickup);

        const hasPendingDelivery = !!ordersBetweenPickups.find(
            order =>
                !('type' in order) &&
                order?.status !== undefined &&
                order.status < ACCOUNT_ORDER_STATUS.delivering &&
                order.depot_id === item.depotId
        );

        if (!hasPendingDelivery) {
            handleSetModified();

            const updatedOrders = [...orders];
            updatedOrders.splice(index, 1);
            setOrders(updatedOrders);

            return;
        }

        modal.error({
            title: t('draft.deletePickupError'),
        });
    };

    const onDelete = (order, index) => {
        if ('type' in order) {
            return deletePickupOrder(order, index);
        }
        deleteDeliveryOrder(order, index);
    }

    const handleSetModified = () => {
        setModified(true);
        setEstimation(false);
    }

    const onDragEnd = (event) => {
        const { active, over } = event;

        if (!over) return;

        const fromIndex = orders.findIndex(o => o.id === active.id);
        const toIndex = orders.findIndex(o => o.id === over.id);

        const draggedOrder = orders[fromIndex];

        if (fromIndex === toIndex) return;

        const data = arrayMove(orders, fromIndex, toIndex);

        if ('type' in draggedOrder) {
            if (toIndex < fromIndex) {
                handleSetModified();

                return setOrders(data);
            }

            const isSameType = data.slice(fromIndex, toIndex).every(order => 'type' in order);

            if (isSameType) {
                handleSetModified();

                return setOrders(data);
            }

            const pickupOrdersBeforeCurrentPickup = orders
                .slice(0, fromIndex)
                .filter((order) => 'type' in order && order.depotId === draggedOrder.depotId);

            const hasPendingPickup = !!pickupOrdersBeforeCurrentPickup.find(
                order => order.state === PICKUP_STATE.pending
            );

            if (hasPendingPickup) {
                handleSetModified();

                return setOrders(data);
            }

            const ordersAfterCurrentPickup = orders.slice(fromIndex + 1);

            const toIndexAfterCurrentPickup = toIndex - (fromIndex + 1);

            let indexOfNextSimilarPickup = ordersAfterCurrentPickup.findIndex(
                order =>
                    'type' in order && order.depotId === draggedOrder.depotId && order.state === PICKUP_STATE.pending
            );

            if (indexOfNextSimilarPickup === -1 || indexOfNextSimilarPickup > toIndexAfterCurrentPickup + 1) {
                indexOfNextSimilarPickup = toIndexAfterCurrentPickup + 1;
            }

            const ordersBetweenPickups = ordersAfterCurrentPickup.slice(0, indexOfNextSimilarPickup);

            const hasPendingDelivery = !!ordersBetweenPickups.find(
                order =>
                    !('type' in order) &&
                    order.depot_id === draggedOrder.depotId &&
                    order?.status !== undefined &&
                    order.status < ACCOUNT_ORDER_STATUS.delivering
            );

            if (!hasPendingDelivery) {
                handleSetModified();

                return setOrders(data);
            }
        }

        if (!('type' in draggedOrder)) {
            if (toIndex > fromIndex) {
                handleSetModified();

                return setOrders(data);
            }

            const isSameType = data.slice(toIndex, fromIndex + 1).every(order => !('type' in order));

            if (isSameType) {
                handleSetModified();

                return setOrders(data);
            }

            const pickupsOrdersBeforeCurrentOrder = data
                .slice(0, toIndex)
                .filter(
                    (order) => 'type' in order && draggedOrder.depot_id === order.depotId
                );

            const hasArrivedPickup = pickupsOrdersBeforeCurrentOrder.find(
                order => order.state === PICKUP_STATE.arrived
            );
            const hasPendingPickup = pickupsOrdersBeforeCurrentOrder.find(
                order => order.state === PICKUP_STATE.pending
            );

            if (
                (draggedOrder.status === ACCOUNT_ORDER_STATUS.delivering && hasArrivedPickup) ||
                (draggedOrder.status !== ACCOUNT_ORDER_STATUS.delivering && hasPendingPickup)
            ) {
                handleSetModified();

                return setOrders(data);
            }
        }
    }

    const markers = orders.concat(courier).map(order => {
        if (order.userId) {
            return Markers.instance.createMarker(TYPE_MARKER.courier, order);
        }
        if ('type' in order) {
            const depot = getDepotByIdMap(order.depotId);
            return Markers.instance.createMarker(TYPE_MARKER.depot, depot);
        }
        return Markers.instance.createMarker(TYPE_MARKER.order, order, { isCourierGroup: false }, null, null, null, { color: courier.color });
    }).reduce((acc, cv) => {
        if (!cv?.id || acc.find(f => f.id === cv?.id)) {
            return acc;
        }
        return [...acc, cv];
    }, []);

    const depots = useMemo(() => {
        const depotIds = orders.filter(order => 'type' in order).map(order => order.depotId);
        return [...depotIds].map(depotId => getDepotByIdMap(depotId));
    }, [orders]);

    return (
        <div className="associatePopupDraft">
            <div className="associatePopupDraftHeader">
                {courier && (
                    <Avatar avatar={courier.avatarUrl} color={courier.color} />
                )}
                <span className="courierInfoTitle">
                    <VehicleIcon value={courier.vehicleType} isActive={true} />
                    {getFullName(courier)}
                </span>

                <IconSwitchControl
                    value={isMapMode}
                    on="iconTakeAway"
                    off="iconMenuList"
                    onSelect={() => setIsMapMode(!isMapMode)}
                />
            </div>
            {contextHolder}
            {!isMapMode && (
                <DndContext onDragEnd={onDragEnd} collisionDetection={closestCorners}>
                    <div className="associatePopupDraftItems">
                        <SortableContext items={orders} strategy={verticalListSortingStrategy}>
                            {orders.map((order, index) => (<GorupbarOrderItemDraft
                                estimatedPriceInfo={(estimatedPriceInfo?.find(o => o.orderID === order.id) || {}).price}
                                onChangeState={onChangeState}
                                showExtraButton={showExtraButton}
                                showEditOrder={showEditOrder}
                                onAssociate={onAssociate}
                                showAssociate={showAssociate}
                                highlight={selectedOrderIds.includes(order.id)}
                                key={`groupbarItemDraft-${order.id}`}
                                showTime={!!estimation}
                                order={order}
                                onClickCreate={() => onAddPickup(order, index)}
                                onClickDelete={() => onDelete(order, index)}
                            />))}
                        </SortableContext>

                    </div>
                </DndContext>
            )}

            {isMapMode && (
                <div className="draftMap">
                    <Map
                        t={t}
                        users={courier ? [courier] : []}
                        orders={orders.filter(order => !('type' in order)).map(order => ({ ...order, depotId: order.depot_id, orderId: order.sequence_id }))}
                        depots={depots}
                        markers={markers.filter(Boolean)}
                        center={null}
                        region={{ locations: markers.filter(Boolean).map(m => ({ ...m.location })) }}
                        circle={[]}
                        isHidePolygon={true}
                        isReadonly={false}
                        renderPolygonsFromBusiness={false}
                        alwaysShowPolygons={false}
                        businessPolygones={[]}
                        boundsByRegion={true}
                        boundsByCircle={false}
                    />
                </div>
            )}

        </div>
    )
}

export default AssociatePopupDraft;